import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";

export const PerformerContext = createContext();

// Synonym map — maps user search terms to internal field values
const SEARCH_SYNONYMS = {
  'latin': 'hispanic', 'latina': 'hispanic', 'latino': 'hispanic', 'mexican': 'hispanic',
  'black': 'ebony', 'african': 'ebony',
  'japanese': 'asian', 'korean': 'asian', 'chinese': 'asian', 'thai': 'asian',
  'white': 'caucasian',
  'arab': 'middleeastern', 'arabic': 'middleeastern', 'middle east': 'middleeastern',
  'blonde': 'blond', 'brunette': 'brown', 'redhead': 'red', 'ginger': 'red',
  'petite': 'petite', 'slim': 'slim', 'skinny': 'slim', 'thick': 'curvy', 'chubby': 'bbw',
  'fit': 'athletic',
  'teen': 'teen', 'young': 'teen', 'milf': 'milf', 'mature': 'mature', 'cougar': 'mature',
  'squirt': 'squirt', 'dom': 'dominat', 'domination': 'dominat', 'femdom': 'femdom',
  'sub': 'submiss', 'feet': 'feet', 'foot': 'feet', 'dirty talk': 'dirty',
  'lesbian': 'lesbian', 'bi': 'bisex', 'bisexual': 'bisex',
  'strip': 'strip', 'tease': 'teas', 'oil': 'oil', 'dance': 'danc',
  'anal': 'anal', 'bdsm': 'bdsm', 'cosplay': 'cosplay', 'roleplay': 'roleplay',
  'dp': 'penetr', 'findom': 'findom', 'sph': 'sph', 'joi': 'joi',
  // Body features
  'big tits': 'bigboob', 'big boobs': 'bigboob', 'busty': 'bigboob', 'huge tits': 'bigboob',
  'big boob': 'bigboob', 'large tits': 'bigboob', 'big breasts': 'bigboob',
  'small tits': 'smallboob', 'flat': 'smallboob', 'small boobs': 'smallboob',
  'big ass': 'bigass', 'big butt': 'bigass', 'booty': 'bigass', 'bubble butt': 'bigass',
  'hairy': 'hairy', 'shaved': 'shaved', 'natural': 'natural',
  'tattoo': 'tattoo', 'tattooed': 'tattoo', 'piercing': 'piercing', 'pierced': 'piercing',
  'pregnant': 'pregnant', 'curvy': 'curvy', 'bbw': 'bbw', 'plus size': 'bbw',
  'muscle': 'muscular', 'muscular': 'muscular', 'athletic': 'athletic',
  'glasses': 'glasses', 'nerd': 'glasses',
  'smoke': 'smoking', 'smoking': 'smoking',
  'dildo': 'toy', 'toy': 'toy', 'vibrator': 'toy',
  'cum': 'cumshow', 'cumshot': 'cumshow', 'cum show': 'cumshow',
};

const scorePerformer = (performer, terms) => {
  const name    = (performer.displayName || performer.name || '').toLowerCase();
  const eth     = (performer.ethnicity || '').toLowerCase();
  const hair    = (performer.hairColor || '').toLowerCase();
  const build   = (performer.build || '').toLowerCase();
  const age     = parseInt(performer.age) || 0;
  const theme   = (performer.theme || '').toLowerCase();
  const fetishes = (Array.isArray(performer.fetishes)
    ? performer.fetishes
    : (performer.fetishes || '').split(',')
  ).map(f => f.trim().toLowerCase()).filter(Boolean);
  const allText = [name, eth, hair, build, theme, ...fetishes].join(' ');

  let score = 0;
  for (const t of terms) {
    if (!t) continue;
    const syn = SEARCH_SYNONYMS[t] || t;

    if (name.includes(t))                                          { score += 10; continue; }
    if (eth === t || eth === syn || eth.includes(syn))             { score += 8;  continue; }
    if (hair === t || hair === syn || hair.includes(t))            { score += 6;  continue; }
    if (build === t || build === syn || build.includes(t))         { score += 6;  continue; }
    if ((t === 'teen' || t === 'young') && age >= 18 && age <= 22) { score += 7;  continue; }
    if ((t === 'milf' || t === 'mature' || t === 'cougar') && age >= 30) { score += 7; continue; }
    if (fetishes.some(f => f.includes(syn) || f.includes(t)))     { score += 7;  continue; }
    if (theme.includes(syn) || theme.includes(t))                  { score += 5;  continue; }
    if (allText.includes(t))                                       { score += 3; }
  }
  return score;
};

export const filterPerformers = (performers, filters = {}) => {
  if (!Array.isArray(performers)) return [];

  const safeFilters = {
    ethnicity: [],
    age: [],
    fetishes: [],
    build: [],
    hairColor: [],
    searchTerm: '',
    ...filters,
  };

  const hasActiveFilters = ['ethnicity','age','fetishes','build','hairColor'].some(
    k => Array.isArray(safeFilters[k]) && safeFilters[k].length > 0
  );
  const rawSearch   = (safeFilters.searchTerm || '').trim();
  const hasSearch   = rawSearch.length > 0;
  const searchTerms = rawSearch.toLowerCase().split(/[\s,]+/).filter(Boolean);

  if (!hasActiveFilters && !hasSearch) return performers;

  let results = performers.filter(performer => {
    // Pill filters
    if (hasActiveFilters) {
      const eth = safeFilters.ethnicity;
      if (eth.length && !eth.some(e => performer.ethnicity?.toLowerCase() === e.toLowerCase())) return false;

      const age = parseInt(performer.age) || 0;
      if (safeFilters.age.length) {
        const ageMatch = safeFilters.age.some(label => {
          switch (label) {
            case 'Teen 18+':   return age >= 18 && age <= 19;
            case '20s':        return age >= 20 && age <= 29;
            case '30s':        return age >= 30 && age <= 39;
            case '40s':        return age >= 40 && age <= 49;
            case 'Mature 50+': return age >= 50;
            default:           return false;
          }
        });
        if (!ageMatch) return false;
      }

      if (safeFilters.fetishes.length) {
        const pf = Array.isArray(performer.fetishes) ? performer.fetishes : (performer.fetishes || '').split(',');
        if (!pf.some(f => safeFilters.fetishes.some(sf => f.trim().toLowerCase() === sf.toLowerCase()))) return false;
      }

      if (safeFilters.hairColor.length) {
        const ph = (performer.hairColor || '').toLowerCase();
        const hairMatch = safeFilters.hairColor.some(h => {
          if (h === 'blonde') return ph === 'blond' || ph === 'blonde';
          if (h === 'brunette') return ph === 'brown' || ph === 'brunette' || ph === 'dark';
          return ph === h;
        });
        if (!hairMatch) return false;
      }
    }

    // Free-text search — must score at least 1
    if (hasSearch) {
      return scorePerformer(performer, searchTerms) > 0;
    }

    return true;
  });

  // Sort by relevance score when searching
  if (hasSearch) {
    results = results
      .map(p => ({ p, score: scorePerformer(p, searchTerms) }))
      .sort((a, b) => b.score - a.score)
      .map(({ p }) => p);
  }

  return results;
};

export const PerformerProvider = ({ children }) => {
  const [performers, setPerformers]       = useState([]);
  const [ratings, setRatings]             = useState({}); // {name: {avg, count}}
  const [selectedFilters, setSelectedFilters] = useState({
    ethnicity: [], age: [], fetishes: [], build: [], hairColor: [], searchTerm: ''
  });
  const [loading, setLoading] = useState(true);
  const [apiSearchResults, setApiSearchResults] = useState([]);
  const [apiSearchLoading, setApiSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  const handleFilterChange = (newFilters) => setSelectedFilters(prev => ({ ...prev, ...newFilters }));

  // API search — fires when local filter/search returns no results
  useEffect(() => {
    const term = (selectedFilters.searchTerm || '').trim();
    const ethFilters  = selectedFilters.ethnicity  || [];
    const hairFilters = selectedFilters.hairColor   || [];
    const ageFilters  = selectedFilters.age         || [];
    const hasFilters  = term.length >= 2 || ethFilters.length > 0 || hairFilters.length > 0 || ageFilters.length > 0;

    if (!hasFilters) { setApiSearchResults([]); setApiSearchLoading(false); return; }

    // Check local hits first
    const localHits = filterPerformers(performers, selectedFilters);
    if (localHits.length >= 3) { setApiSearchResults([]); setApiSearchLoading(false); return; }

    // Build API params from active filters
    const params = { limit: 24, exact: false };
    if (term.length >= 2) params.keyword = term;
    if (ethFilters.length > 0) {
      // Map pill labels to Streamate ethnicity values
      const ethMap = { latina: 'hispanic', ebony: 'ebony', asian: 'asian', white: 'caucasian', 'middle eastern': 'middleeastern', indian: 'indian', latin: 'hispanic' };
      const mapped = ethFilters.map(e => ethMap[e.toLowerCase()] || e).join(',');
      params.ethnicity = mapped;
    }
    if (hairFilters.length > 0) {
      const hairMap = { blonde: 'blond', brunette: 'brown' };
      params.hairColor = hairFilters.map(h => hairMap[h.toLowerCase()] || h).join(',');
    }
    if (ageFilters.length > 0) {
      // Pick broadest range from selected age pills
      const ageRangeMap = { 'Teen 18+': [18,19], '20s': [20,29], '30s': [30,39], '40s': [40,49], 'Mature 50+': [50,99] };
      const mins = ageFilters.map(a => ageRangeMap[a]?.[0]).filter(Boolean);
      const maxs = ageFilters.map(a => ageRangeMap[a]?.[1]).filter(Boolean);
      if (mins.length) params.ageMin = Math.min(...mins);
      if (maxs.length) params.ageMax = Math.max(...maxs);
    }

    setApiSearchLoading(true);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, { params });
        const list = (Array.isArray(res.data) ? res.data : []).map(performer => {
          const attrs = performer['@attributes'] || {};
          return {
            uniqueModelId: attrs.Id,
            performerId: attrs.Name,
            displayName: attrs.Name,
            status: 'free_chat',
            ethnicity: attrs.Ethnicity || 'N/A',
            fetishes: attrs.Fetishes ? attrs.Fetishes.split(',').map(f => f.trim()) : [],
            age: attrs.Age || 'N/A',
            hairColor: attrs.HairColor || '',
            profilePictureUrl: (performer.Media?.Pic?.Thumb?.['@attributes']?.Src || '').replace(/^\/\//, 'https://'),
            profilePictureFullUrl: (performer.Media?.Pic?.Full?.['@attributes']?.Src || '').replace(/^\/\//, 'https://'),
            chatRoomUrl: `https://instacamgirlslive.com/cam/${attrs.Name}/?smid=${attrs.Id}&AFNO=1-94033`,
            embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${attrs.Name}&widescreen=true&AFNO=1-94033`,
          };
        });
        setApiSearchResults(list);
      } catch (e) {
        setApiSearchResults([]);
      } finally {
        setApiSearchLoading(false);
      }
    }, 400);
  }, [selectedFilters, performers]);

  useEffect(() => {
    const fetchPerformers = async () => {
      try {
        const ts = new Date().getTime();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/performers?t=${ts}`
        );

        if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
          setPerformers([]);
          setLoading(false);
          return;
        }

        const performersData = response.data.map((performer) => {
          const attrs = performer['@attributes'] || {};

          // Parse photo gallery — array of {full, thumb}
          let gallery = [];
          const galleryRaw = performer?.Media?.PhotoGallery?.Pic;
          if (galleryRaw) {
            const pics = Array.isArray(galleryRaw) ? galleryRaw : [galleryRaw];
            gallery = pics.map(p => ({
              full:  (p?.Full?.['@attributes']?.Src  || p?.Full?.Src  || '').replace(/^\/\//, 'https://'),
              thumb: (p?.Thumb?.['@attributes']?.Src || p?.Thumb?.Src || '').replace(/^\/\//, 'https://'),
            })).filter(p => p.full);
          }

          // Descriptions — API returns [] when empty, must coerce to string
          const descriptions = performer?.Descriptions || {};
          const about    = Array.isArray(descriptions?.About)    ? '' : (descriptions?.About    || '');
          const turnOns  = Array.isArray(descriptions?.TurnOns)  ? '' : (descriptions?.TurnOns  || '');
          const expertise = Array.isArray(descriptions?.Expertise) ? '' : (descriptions?.Expertise || '');

          return {
            uniqueModelId:       attrs.Id,
            performerId:         attrs.Name,
            displayName:         attrs.Name,
            status:              attrs.StreamType === 'live' ? 'free_chat' : 'offline',
            ethnicity:           attrs.Ethnicity   || 'N/A',
            fetishes:            attrs.Fetishes    ? attrs.Fetishes.split(',').map(f => f.trim()) : [],
            age:                 attrs.Age         || 'N/A',
            hairColor:           attrs.HairColor   || '',
            eyeColor:            attrs.EyeColor    || '',
            build:               attrs.Build       || '',
            theme:               attrs.Theme       || '',
            cupSize:             attrs.CupSize     || '',
            language:            attrs.Language    || '',
            sexPref:             attrs.SexPref     || '',
            zodiac:              attrs.Zodiac      || '',
            bodyMods:            attrs.BodyMods    || '',
            pubicHair:           attrs.PubicHair   || '',
            audio:               attrs.Audio === 'true',
            freeChatAudio:       attrs.FreeChatAudio === 'true',
            goldShow:            !!(attrs.PerfFlag && (parseInt(attrs.PerfFlag) & 0x00100000)),
            hdStream:            !!(attrs.PerfFlag && (parseInt(attrs.PerfFlag) & 0x10000000)),
            widescreen:          !!(attrs.PerfFlag && (parseInt(attrs.PerfFlag) & 0x00000800)),
            about,
            turnOns,
            expertise,
            gallery,
            details: {
              willingnesses: attrs.Fetishes ? attrs.Fetishes.split(',').map(f => f.trim()) : []
            },
            profilePictureUrl:      (performer.Media?.Pic?.Thumb?.['@attributes']?.Src || '').replace(/^\/\//, 'https://'),
            profilePictureFullUrl:  (performer.Media?.Pic?.Full?.['@attributes']?.Src  || '').replace(/^\/\//, 'https://'),
            // Streamate cam URL with correct affiliate ID
            chatRoomUrl: `https://instacamgirlslive.com/cam/${attrs.Name}/?smid=${attrs.Id}&AFNO=1-94033`,
            // Hybrid embed player URL (AFNO required for affiliate tracking)
            embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${attrs.Name}&widescreen=true&AFNO=1-94033`,
          };
        });

        // Cache for profile page offline fallback (keyed by name)
        try {
          const cache = {};
          performersData.forEach(p => { if (p.displayName) cache[p.displayName] = p; });
          localStorage.setItem('icg_performer_cache', JSON.stringify(cache));
          localStorage.setItem('icg_performer_cache_ts', Date.now().toString());
        } catch(e) {}

        // Save all performers to persistent directory (fire & forget)
        try {
          const batch = performersData.map(p => ({
            name: p.displayName, displayName: p.displayName, network: 'streamate',
            ethnicity: p.ethnicity, hairColor: p.hairColor, eyeColor: p.eyeColor,
            age: p.age, build: p.build, cupSize: p.cupSize, language: p.language,
            theme: p.theme, fetishes: Array.isArray(p.fetishes) ? p.fetishes.join(',') : (p.fetishes || ''),
            goldShow: p.goldShow, hdStream: p.hdStream,
            profilePictureUrl: p.profilePictureUrl,
            chatRoomUrl: p.chatRoomUrl, embedUrl: p.embedUrl,
          }));
          fetch(`${process.env.REACT_APP_API_URL}/api/performers-directory/upsert`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ performers: batch }),
          }).catch(() => {});
        } catch(e) {}

        // Fetch ratings and boost top-rated performers to the front
        try {
          const rRes = await fetch(`${process.env.REACT_APP_API_URL}/api/performer-ratings`);
          const rMap = await rRes.json();
          setRatings(rMap);
          // Sort: rated performers (by avg desc) first, then unrated
          if (Object.keys(rMap).length > 0) {
            performersData.sort((a, b) => {
              const ra = rMap[a.displayName];
              const rb = rMap[b.displayName];
              if (ra && rb) return rb.avg - ra.avg;
              if (ra) return -1; // rated goes first
              if (rb) return 1;
              return 0; // both unrated — keep original order
            });
          }
        } catch(e) {}

        setPerformers(performersData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching performers:', error);
        setPerformers([]);
        setLoading(false);
      }
    };

    fetchPerformers();
  }, []);

  return (
    <PerformerContext.Provider value={{ performers, selectedFilters, loading, handleFilterChange, ratings, apiSearchResults, apiSearchLoading }}>
      {children}
    </PerformerContext.Provider>
  );
};

export default PerformerProvider;
